import React, { useState } from "react";
import { COURSES, CourseDef, coursesForRole } from "../data/trainingCourses";

interface TrainingRecord {
  id: string;
  staffId: string;
  staffName: string;
  courseId: string;
  courseName: string;
  completedDate: string;
  expiryDate: string;
  provider: string;
}

interface StaffLite {
  id: string;
  name: string;
  role: string;
}

const RECORDS_KEY = "training_records";
const STAFF_KEY = "staff_records";

function loadRecords(): TrainingRecord[] {
  try {
    return JSON.parse(localStorage.getItem(RECORDS_KEY) || "[]");
  } catch (error) {
    return [];
  }
}

function loadStaff(): StaffLite[] {
  try {
    const raw = JSON.parse(localStorage.getItem(STAFF_KEY) || "[]");

    return raw.map((item: any) => ({
      id: item.id,
      name: (item.data && item.data.name) || "Unnamed",
      role: (item.data && item.data.role) || "Carer",
    }));
  } catch (error) {
    return [];
  }
}

type CellStatus = "none" | "done" | "valid" | "due" | "overdue";

function statusFor(records: TrainingRecord[], staffId: string, course: CourseDef): CellStatus {
  const matches = records.filter(r => r.staffId === staffId && r.courseId === course.id);

  if (matches.length === 0) return "none";

  const latest = matches.reduce((a, b) => (a.completedDate > b.completedDate ? a : b));

  if (!course.months) return "done";

  const expiry = new Date(latest.expiryDate || latest.completedDate).getTime();
  const days = Math.ceil((expiry - Date.now()) / 86400000);

  if (days < 0) return "overdue";
  if (days <= 60) return "due";

  return "valid";
}

const STATUS_STYLE: Record<CellStatus, string> = {
  none: "bg-slate-200",
  done: "bg-green-600",
  valid: "bg-green-600",
  due: "bg-amber-400",
  overdue: "bg-red-600",
};

export default function Training({ user }: { user?: any }) {
  const [tab, setTab] = useState<"matrix" | "record" | "due">("matrix");
  const [records, setRecords] = useState<TrainingRecord[]>(loadRecords);
  const [staff, setStaff] = useState<StaffLite[]>(loadStaff);
  const [group, setGroup] = useState<string>("Mandatory");
  const [staffId, setStaffId] = useState("");
  const [courseId, setCourseId] = useState("");
  const [completedDate, setCompletedDate] = useState(new Date().toISOString().slice(0, 10));
  const [provider, setProvider] = useState("");
  const [message, setMessage] = useState("");

  const addRecord = () => {
    const person = staff.find(s => s.id === staffId);
    const course = COURSES.find(c => c.id === courseId);

    if (!person || !course) {
      setMessage("Choose a staff member and a course.");
      return;
    }

    let expiry = "";

    if (course.months > 0) {
      const d = new Date(completedDate);
      d.setMonth(d.getMonth() + course.months);
      expiry = d.toISOString().slice(0, 10);
    }

    const record: TrainingRecord = {
      id: `tr-${Date.now()}`,
      staffId: person.id,
      staffName: person.name,
      courseId: course.id,
      courseName: course.name,
      completedDate,
      expiryDate: expiry,
      provider,
    };

    const next = [record, ...records];

    setRecords(next);
    localStorage.setItem(RECORDS_KEY, JSON.stringify(next));
    setMessage(`Training recorded for ${person.name}: ${course.name}.`);
  };

  const dueItems = staff.flatMap(person =>
    coursesForRole(person.role)
      .map(course => ({ person, course, status: statusFor(records, person.id, course) }))
      .filter(row => row.status === "overdue" || row.status === "due" || row.status === "none")
  );
  const groupCourses = COURSES.filter(course => course.group === group);

  const complianceFor = (person: StaffLite) => {
    const applicable = coursesForRole(person.role).filter(course => course.group === "Mandatory");

    if (applicable.length === 0) return 100;

    const ok = applicable.filter(course => {
      const status = statusFor(records, person.id, course);

      return status === "valid" || status === "done";
    }).length;

    return Math.round((ok / applicable.length) * 100);
  };

  return (
    <div className="space-y-6 p-6">
      <div>
        <h2 className="text-xl font-semibold text-slate-900">Training Matrix</h2>
        <p className="text-sm text-slate-600">Full mandatory, clinical, role specific and development training with automatic expiry tracking per staff member.</p>
      </div>

      <div className="flex flex-wrap gap-2">
        <button onClick={() => setTab("matrix")} className={tab === "matrix" ? "rounded-lg bg-blue-900 px-4 py-2 text-sm font-medium text-white" : "rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700"}>Matrix</button>
        <button onClick={() => setTab("record")} className={tab === "record" ? "rounded-lg bg-blue-900 px-4 py-2 text-sm font-medium text-white" : "rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700"}>Record Training</button>
        <button onClick={() => setTab("due")} className={tab === "due" ? "rounded-lg bg-blue-900 px-4 py-2 text-sm font-medium text-white" : "rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700"}>Due and Overdue ({dueItems.length})</button>
      </div>

      {message ? <div className="rounded-lg bg-blue-50 p-3 text-sm text-blue-900">{message}</div> : null}

      {tab === "matrix" ? (
        <div className="space-y-4">
          <div className="flex flex-wrap items-center gap-3">
            <select value={group} onChange={event => setGroup(event.target.value)} className="rounded-lg border border-slate-300 px-3 py-2 text-sm">
              <option>Mandatory</option>
              <option>Clinical</option>
              <option>Role Specific</option>
              <option>Development</option>
            </select>
            <div className="flex flex-wrap items-center gap-3 text-xs text-slate-600">
              <span className="flex items-center gap-1"><span className="h-3 w-3 rounded bg-green-600" /> Current</span>
              <span className="flex items-center gap-1"><span className="h-3 w-3 rounded bg-amber-400" /> Due soon</span>
              <span className="flex items-center gap-1"><span className="h-3 w-3 rounded bg-red-600" /> Overdue</span>
              <span className="flex items-center gap-1"><span className="h-3 w-3 rounded bg-slate-200" /> Not recorded</span>
            </div>
          </div>

          {staff.length === 0 ? <div className="rounded-lg bg-amber-50 p-3 text-sm text-amber-800">Add staff in Staff Management first, then record their training here.</div> : null}

          <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white shadow-sm">
            <table className="w-full text-left text-xs">
              <thead className="border-b border-slate-200 bg-slate-50">
                <tr>
                  <th className="px-3 py-2 font-medium text-slate-700">Staff member</th>
                  <th className="px-3 py-2 font-medium text-slate-700">Mandatory %</th>
                  {groupCourses.map(course => (
                    <th key={course.id} title={course.name} className="px-1 py-2 text-center font-medium text-slate-600">
                      {course.id.replace(/-/g, " ").slice(0, 8)}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {staff.map(person => (
                  <tr key={person.id} className="border-b border-slate-100">
                    <td className="px-3 py-2 font-medium text-slate-800">
                      {person.name}
                      <div className="text-[10px] text-slate-500">{person.role}</div>
                    </td>
                    <td className="px-3 py-2 font-semibold text-slate-700">{complianceFor(person)}%</td>
                    {groupCourses.map(course => {
                      const applicable = coursesForRole(person.role).some(item => item.id === course.id);
                      const status = statusFor(records, person.id, course);

                      return (
                        <td key={course.id} className="px-1 py-2 text-center">
                          {applicable ? <span title={course.name} className={`inline-block h-3 w-3 rounded ${STATUS_STYLE[status]}`} /> : <span className="text-slate-300">-</span>}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : null}
      {tab === "record" ? (
        <div className="max-w-xl space-y-3 rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
          <select value={staffId} onChange={event => setStaffId(event.target.value)} className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm">
            <option value="">Choose staff member</option>
            {staff.map(person => <option key={person.id} value={person.id}>{person.name} ({person.role})</option>)}
          </select>
          <select value={courseId} onChange={event => setCourseId(event.target.value)} className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm">
            <option value="">Choose course</option>
            {["Mandatory", "Clinical", "Role Specific", "Development"].map(groupName => (
              <optgroup key={groupName} label={groupName}>
                {COURSES.filter(course => course.group === groupName).map(course => <option key={course.id} value={course.id}>{course.name}</option>)}
              </optgroup>
            ))}
          </select>
          <input type="date" value={completedDate} onChange={event => setCompletedDate(event.target.value)} className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm" />
          <input value={provider} onChange={event => setProvider(event.target.value)} placeholder="Training provider or trainer" className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm" />
          <button onClick={addRecord} className="rounded-lg bg-green-700 px-4 py-2 text-sm font-medium text-white">Save training record</button>
        </div>
      ) : null}

      {tab === "due" ? (
        <div className="space-y-3">
          {dueItems.map(row => (
            <div key={`${row.person.id}-${row.course.id}`} className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <div>
                <div className="font-medium text-slate-900">{row.person.name}</div>
                <div className="text-xs text-slate-500">{row.course.name} ({row.course.group})</div>
              </div>
              <span className={row.status === "overdue" ? "rounded-full bg-red-100 px-2 py-0.5 text-xs font-medium text-red-700" : row.status === "due" ? "rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-700" : "rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-600"}>
                {row.status === "overdue" ? "Overdue" : row.status === "due" ? "Due within 60 days" : "Not recorded"}
              </span>
            </div>
          ))}
          {dueItems.length === 0 ? <div className="rounded-lg bg-green-50 p-3 text-sm text-green-800">All required training is up to date.</div> : null}
        </div>
      ) : null}
    </div>
  );
}
